/* eslint-disable max-len */
import { useEffect, useState } from 'react';
import { hooks as commonHooks } from '@sitebuilder/common';
import { isLocalStorageAccessible } from '../utils';
import { ColumnOption, SelectionListData } from './CustomizedFundList';
import SortComp from './SortComp';
import TableRow from './TableRow';
import TableRowGroup from './TableRowGroup';

interface TableProps {
    columns: ColumnOption[];
    rows: any;
    data: any;
    widgetInstance: any;
    enableWatchlist: boolean;
    watchlist: string[];
    setWatchlist: (checklist: string[]) => void;
    watchlistStorageName?: string;
    watchlistDataId?: string;
    addToWatchlistText: string;
    removeFromWatchlistText: string;
    onChangeSorting: any;
    enableGrouping: boolean;
    groupingPropertyId: string;
    groupsCount: Record<string, number>;
    showGroupingCount: boolean;
    isCollapseRowGroup: boolean;
    showRowGroupIconName: string;
    hideRowGroupIconName: string;
    mobileBreakpoint?: string;
    onClickCompare: (storageName: string, id: string, colId: string, changeToTab?: string) => any;
    onChangeSelectionLists: (data: Record<string, string[]>) => any;
    selectionLists: Record<string, string[]>;
    filterBySelection: SelectionListData[];
    selectedTab?: string;
    staticDataTabIndex: number;
    sorting: string;
    defaultSortingOrder: string;
    columnGroups?: {
        id: string;
        label: string;
    }[];
    onTriggerTracking: (
        event: string,
        currentTab?: string,
        fundName?: string,
        filterAnalyticLabel?: string,
        customValue?: string
    ) => any;
    useAnalytics: boolean;
    downloadBooklet?: boolean;
    rowsDataBeforePagination?: any;
}

const Table = (props: TableProps) => {
    const {
        columns,
        rows,
        data,
        widgetInstance,
        onChangeSorting,
        setWatchlist,
        watchlist,
        watchlistDataId,
        enableGrouping,
        groupingPropertyId,
        columnGroups,
        ...rest
    } = props;

    const mobileBreakpoint = rest.mobileBreakpoint || '768px';
    const i18n = commonHooks.useLocalization();
    const { isClient } = commonHooks.useAppContext();

    function isMobileWidth() {
        if (isClient && window.matchMedia) {
            return window.matchMedia(`(min-width: ${mobileBreakpoint})`).matches;
        }
        return true; // Default value if window or matchMedia is not available
    }

    const [sortId, setSortId] = useState('');
    const [isMobile, setIsMobile] = useState(isMobileWidth());
    const [selectAll, setSelectAll] = useState(false);
    const _isLocalStorageAccessible: boolean = isLocalStorageAccessible();

    const WATCHLIST = rest.watchlistStorageName || 'watchlist';

    useEffect(() => {
        setIsMobile(window.matchMedia(`(min-width: ${mobileBreakpoint})`)?.matches);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // check whether item is already added to watchlist or not
        const selectedListItems: any = _isLocalStorageAccessible
            ? localStorage.getItem(WATCHLIST)
            : [''];

        if (selectedListItems && selectedListItems.length) {
            setWatchlist(JSON.parse(selectedListItems));
        } else {
            setWatchlist([]);
        }
        const handleResize = () =>
            setIsMobile(window.matchMedia(`(min-width: ${mobileBreakpoint})`).matches);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data[watchlistDataId || 'id'], _isLocalStorageAccessible]);

    useEffect(() => {
        // check whether item is already added to selectionlists or not
        let tmp = rest.selectionLists;
        columns?.forEach((c) => {
            if (c.type === 'Selection') {
                const storageName: string = c.selectionListStorageName || '';
                const listItems: string[] = JSON.parse(localStorage.getItem(storageName) || '[]');

                if (listItems && listItems.length) {
                    tmp = {
                        ...tmp,
                        [storageName]: listItems,
                    };
                } else {
                    // const tmp: Record<string, string[]> = rest.selectionLists;
                    delete tmp[storageName];
                }
            }
        });
        rest.onChangeSelectionLists(tmp);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columns, _isLocalStorageAccessible]);

    const onChangeSort = (sorting: string, sortId: string, sortAnalyticLabel: string) => {
        setSortId(sortId);
        onChangeSorting(sorting, sortId, sortAnalyticLabel);
    };

    const doColumnsHaveGrouping = columns?.some(
        (c) =>
            c.columnGrouping && columnGroups && columnGroups.find((g) => g.id === c.columnGrouping)
    );

    const columnsBeforeGrouping: ColumnOption[] = [];
    const groupedColumns: ColumnOption[] = [];
    const columnsAfterGrouping: ColumnOption[] = [];

    if (doColumnsHaveGrouping) {
        let groupedColumnsAreStored = false;

        columns.forEach((c) => {
            if (
                c.columnGrouping &&
                columnGroups &&
                columnGroups.find((g) => g.id === c.columnGrouping)
            ) {
                groupedColumns.push(c);
                groupedColumnsAreStored = true;
            } else {
                groupedColumnsAreStored
                    ? columnsAfterGrouping.push(c)
                    : columnsBeforeGrouping.push(c);
            }
        });
    }

    const bodyElements =
        enableGrouping && groupingPropertyId && rows && Object.keys(rows)?.length
            ? Object.keys(rows).map((group, ind: number) => (
                  <TableRowGroup
                      label={group}
                      columns={columns}
                      rows={rows[group]}
                      isCollapseRowGroup={rest.isCollapseRowGroup}
                      showRowGroupIconName={rest.showRowGroupIconName}
                      hideRowGroupIconName={rest.hideRowGroupIconName}
                      groupsCount={rest.groupsCount}
                      showGroupingCount={rest.showGroupingCount}
                      widgetInstance={widgetInstance}
                      enableWatchlist={rest.enableWatchlist}
                      setWatchlist={setWatchlist}
                      watchlistStorageName={rest.watchlistStorageName}
                      watchlistDataId={watchlistDataId}
                      addToWatchlistText={rest.addToWatchlistText}
                      removeFromWatchlistText={rest.removeFromWatchlistText}
                      watchlist={watchlist}
                      data={data}
                      index={ind}
                      isMobile={isMobile}
                      onChangeSelectionLists={rest.onChangeSelectionLists}
                      selectionLists={rest.selectionLists}
                      key={ind}
                      filterBySelection={rest.filterBySelection}
                      onClickCompare={rest.onClickCompare}
                      staticDataTabIndex={rest.staticDataTabIndex}
                      onTriggerTracking={rest.onTriggerTracking}
                      useAnalytics={rest.useAnalytics}
                      selectedTab={rest.selectedTab || ''}
                  />
              ))
            : rows?.length
              ? rows?.map((r: any, index: number) => (
                    <TableRow
                        data={data}
                        columns={columns}
                        widgetInstance={widgetInstance}
                        enableWatchlist={rest.enableWatchlist}
                        setWatchlist={setWatchlist}
                        watchlistStorageName={rest.watchlistStorageName}
                        watchlistDataId={watchlistDataId}
                        addToWatchlistText={rest.addToWatchlistText}
                        removeFromWatchlistText={rest.removeFromWatchlistText}
                        watchlist={watchlist}
                        r={r}
                        index={index}
                        isMobile={isMobile}
                        onChangeSelectionLists={rest.onChangeSelectionLists}
                        selectionLists={rest.selectionLists}
                        key={index}
                        filterBySelection={rest.filterBySelection}
                        onClickCompare={rest.onClickCompare}
                        staticDataTabIndex={rest.staticDataTabIndex}
                        onTriggerTracking={rest.onTriggerTracking}
                        useAnalytics={rest.useAnalytics}
                        selectedTab={rest.selectedTab || ''}
                    />
                ))
              : null;

    const columnGroupsToShow = columnGroups?.filter((g) =>
        groupedColumns?.find((d) => d.columnGrouping === g.id)
    );

    const onSelectAll = (storageName: string, dataId: string, maxCount?: number) => {
        const allItems = rest?.rowsDataBeforePagination?.map((row: any) => row[dataId || 'id']);

        if (selectAll) {
            // If already selected, clear the list
            rest.onChangeSelectionLists({
                ...rest.selectionLists,
                [storageName]: [],
            });
        } else {
            // Add all items to the list, respecting maxCount if provided
            const updatedItems =
                typeof maxCount === 'number' ? allItems.slice(0, maxCount) : allItems;
            rest.onChangeSelectionLists({
                ...rest.selectionLists,
                [storageName]: updatedItems,
            });
        }

        setSelectAll(!selectAll);
    };

    return (
        <>
            {isMobile ? (
                <table className={`table ${rest.selectedTab}`}>
                    <thead>
                        {doColumnsHaveGrouping && (
                            <tr role="row">
                                {columnsBeforeGrouping.length && (
                                    <th
                                        aria-label="Columns before grouping"
                                        colSpan={columnsBeforeGrouping.length}
                                    />
                                )}

                                {columnGroupsToShow &&
                                    columnGroupsToShow.map((g) => (
                                        <th
                                            colSpan={
                                                groupedColumns?.filter(
                                                    (d) => d.columnGrouping === g.id
                                                ).length
                                            }
                                            className="column-groups"
                                            role="columnheader"
                                        >
                                            <span key={g.id} className={`column-group-toggle`}>
                                                {i18n.format(g.label, data)}
                                            </span>
                                        </th>
                                    ))}
                                {columnsAfterGrouping.length && (
                                    <th
                                        aria-label="Columns after grouping"
                                        colSpan={columnsAfterGrouping.length}
                                    />
                                )}
                            </tr>
                        )}
                        <tr>
                            {columns?.map((c: any, index: number) => (
                                <th key={index}>
                                    {c?.type !== 'Selection'
                                        ? c?.isSortable &&
                                          c?.iconPosition === 'Left' && (
                                              <SortComp
                                                  className={'left-icon'}
                                                  column={c}
                                                  selectedSortId={sortId}
                                                  onChangeSorting={onChangeSort}
                                                  sorting={rest.sorting}
                                                  defaultSortingOrder={rest.defaultSortingOrder}
                                              />
                                          )
                                        : ''}
                                    {c?.type !== 'Selection' ? (
                                        <span
                                            tabIndex={rest.staticDataTabIndex}
                                            dangerouslySetInnerHTML={{
                                                __html: i18n.format(c.label, data),
                                            }}
                                        />
                                    ) : c?.selectionUsedFor === 'compare' ? (
                                        <div>
                                            <button
                                                disabled={
                                                    !rest.selectionLists[c.selectionListStorageName]
                                                        ?.length ||
                                                    rest.selectionLists[c.selectionListStorageName]
                                                        ?.length < c.minSelectionCount
                                                }
                                                className={`select-button ${
                                                    rest.filterBySelection?.filter(
                                                        (d) => d.colId === c.id
                                                    )?.length
                                                        ? 'selected'
                                                        : ''
                                                }`}
                                                data-testid={'select-button'}
                                                onClick={() => {
                                                    rest.selectionLists[c.selectionListStorageName]
                                                        ?.length &&
                                                        rest.onClickCompare(
                                                            c.selectionListStorageName,
                                                            c.selectionListId,
                                                            c.id,
                                                            c.changeToTab
                                                        );
                                                }}
                                            >
                                                {rest.filterBySelection?.findIndex(
                                                    (d) =>
                                                        d.storageName === c.selectionListStorageName
                                                )
                                                    ? i18n.format(c.label, data)
                                                    : i18n.format(c.ActiveButtonText, data)}
                                            </button>
                                            {c.labelDescription && (
                                                <div className="compare-btn-description">
                                                    {i18n.format(c.labelDescription, data)}
                                                </div>
                                            )}
                                        </div>
                                    ) : c?.selectionUsedFor === 'downloadBooklet' ? (
                                        <input
                                            className={'select-checkbox'}
                                            data-testid={'select-checkbox'}
                                            type="checkbox"
                                            checked={selectAll}
                                            onChange={() => {
                                                onSelectAll(
                                                    c.selectionListStorageName,
                                                    c.selectionListId,
                                                    c.maxSelectionCount
                                                );
                                            }}
                                        />
                                    ) : (
                                        ''
                                    )}
                                    {c?.type !== 'Selection'
                                        ? c?.isSortable &&
                                          c?.iconPosition === 'Right' && (
                                              <SortComp
                                                  className={'right-icon'}
                                                  column={c}
                                                  selectedSortId={sortId}
                                                  onChangeSorting={onChangeSort}
                                                  sorting={rest.sorting}
                                                  defaultSortingOrder={rest.defaultSortingOrder}
                                              />
                                          )
                                        : ''}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>{bodyElements}</tbody>
                </table>
            ) : (
                <>{bodyElements}</>
            )}
        </>
    );
};

export default Table;
